/**
 * Tenant branding → CSS custom properties (Phase 1b §3.4).
 *
 * Called once at app boot with `tenant.branding` from GET /api/me/features.
 * Everything downstream — Tailwind classes, components, shadcn primitives —
 * reads the resulting custom properties and never sees a branding object.
 *
 * ── THE PROBLEM THIS MODULE ACTUALLY SOLVES ─────────────────────────────────
 * `branding.primary_color` is an arbitrary hex chosen by a contractor or by
 * whoever built their logo. It is NOT guaranteed to be dark, saturated, or
 * anything else. A dashboard that hard-codes white button text works fine
 * until a tenant picks #FFD400 and every primary button becomes unreadable —
 * and that tenant is precisely the one who will not report it as a bug, they
 * will just conclude the product looks broken.
 *
 * So the foreground colour is DERIVED from the brand's own luminance rather
 * than assumed, using the WCAG contrast formula. That single decision is what
 * makes "branding-aware from day one" a real property instead of a hope, and
 * it is what Phase 5 white-label rests on: reskinning is a data change because
 * legibility is computed, not designed per tenant.
 *
 * @typedef {object} Branding
 * @property {string} [logo_url]
 * @property {string} [primary_color]
 * @property {string} [secondary_color]
 * @property {string} [business_name]
 * @property {string} [favicon_url]
 * @property {string} [font_preference]
 */

/**
 * Parse a hex colour in any of the forms a human or a form field produces.
 * Returns null for anything unparseable, so a bad value falls back to the
 * MBOS defaults in index.css rather than writing `NaN` into the theme.
 *
 * @param {string|undefined|null} input
 * @returns {{r: number, g: number, b: number}|null}
 */
export function parseHex (input) {
  if (typeof input !== 'string') return null
  let hex = input.trim().replace(/^#/, '')
  if (hex.length === 3) hex = hex.split('').map(c => c + c).join('')
  if (!/^[0-9a-fA-F]{6}$/.test(hex)) return null
  return {
    r: parseInt(hex.slice(0, 2), 16),
    g: parseInt(hex.slice(2, 4), 16),
    b: parseInt(hex.slice(4, 6), 16)
  }
}

/**
 * WCAG 2.1 relative luminance. The gamma-expansion step is not optional —
 * a naive (r+g+b)/3 gets yellow badly wrong, which is exactly the colour that
 * breaks naive contrast logic.
 *
 * @param {{r: number, g: number, b: number}} rgb
 * @returns {number} 0 (black) to 1 (white)
 */
export function relativeLuminance ({ r, g, b }) {
  const channel = v => {
    const s = v / 255
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
}

/** WCAG contrast ratio between two luminances. 1 = identical, 21 = max. */
function contrast (l1, l2) {
  const [hi, lo] = l1 > l2 ? [l1, l2] : [l2, l1]
  return (hi + 0.05) / (lo + 0.05)
}

/**
 * Pick the foreground that is actually readable on a given background.
 *
 * Not "white unless the colour is light" — an explicit contrast comparison
 * against both candidates, taking whichever wins. On a mid-tone brand where
 * neither reaches AA, this still returns the better of the two rather than
 * silently shipping the worse one.
 *
 * @param {{r: number, g: number, b: number}} rgb
 * @returns {{ink: string, meetsAA: boolean}} ink as `H S% L%` channels
 */
export function readableInk (rgb) {
  const bg = relativeLuminance(rgb)
  const onWhite = contrast(bg, 1)
  const onBlack = contrast(bg, 0)
  const useWhite = onWhite >= onBlack
  return {
    ink: useWhite ? '0 0% 100%' : '0 0% 9%',
    meetsAA: Math.max(onWhite, onBlack) >= 4.5
  }
}

/**
 * RGB → HSL channels, formatted for a CSS custom property.
 * Returned as `H S% L%` (space separated, no wrapper) so Tailwind can apply
 * opacity modifiers to it — `bg-brand/10` needs channels, not `hsl(...)`.
 *
 * @returns {{h: number, s: number, l: number, css: string}}
 */
export function toHsl ({ r, g, b }) {
  const rn = r / 255
  const gn = g / 255
  const bn = b / 255
  const max = Math.max(rn, gn, bn)
  const min = Math.min(rn, gn, bn)
  const d = max - min
  const l = (max + min) / 2

  let h = 0
  let s = 0
  if (d !== 0) {
    s = d / (1 - Math.abs(2 * l - 1))
    switch (max) {
      case rn: h = ((gn - bn) / d) % 6; break
      case gn: h = (bn - rn) / d + 2; break
      default: h = (rn - gn) / d + 4
    }
    h *= 60
    if (h < 0) h += 360
  }

  const round = n => Math.round(n * 10) / 10
  const H = round(h)
  const S = round(s * 100)
  const L = round(l * 100)
  return { h: H, s: S, l: L, css: `${H} ${S}% ${L}%` }
}

/** Shift lightness by a delta, clamped. Used for hover and subtle variants. */
function shiftL ({ h, s, l }, delta) {
  const next = Math.min(100, Math.max(0, l + delta))
  return `${h} ${s}% ${Math.round(next * 10) / 10}%`
}

/**
 * Write a tenant's branding onto :root.
 *
 * Every field is optional and every fallback is the MBOS default already in
 * index.css — a tenant with `branding: {}` (the common case on day one) gets a
 * complete, deliberate-looking theme rather than a half-applied one.
 *
 * @param {Branding|null|undefined} branding
 * @param {HTMLElement} [target]
 * @returns {{brandApplied: boolean, contrastWarning: boolean}}
 */
export function applyBranding (branding, target = document.documentElement) {
  const result = { brandApplied: false, contrastWarning: false }
  if (!branding) return result

  const primary = parseHex(branding.primary_color)
  if (primary) {
    const hsl = toHsl(primary)
    const { ink, meetsAA } = readableInk(primary)

    target.style.setProperty('--brand', hsl.css)
    // Hover moves AWAY from the midpoint: darker for light brands, lighter for
    // dark ones. A fixed "10% darker" turns a near-black brand into a
    // no-op hover state, which reads as an unresponsive button.
    target.style.setProperty('--brand-hover', shiftL(hsl, hsl.l > 50 ? -8 : 8))
    // A tint for selected rows and quiet badges. Anchored near-white rather
    // than derived by lightening, so a desaturated brand still yields a tint
    // that reads as a tint instead of as grey.
    target.style.setProperty('--brand-subtle', `${hsl.h} ${Math.max(hsl.s, 40)}% 96%`)
    target.style.setProperty('--brand-ink', ink)

    result.brandApplied = true
    result.contrastWarning = !meetsAA

    if (!meetsAA && import.meta.env.DEV) {
      // Loud in development, silent in production: this is a data-quality
      // problem for whoever set the branding, not something to shout at a
      // contractor mid-job.
      console.warn(
        `[branding] primary_color ${branding.primary_color} cannot reach WCAG AA ` +
        'against either black or white text. The more readable of the two was ' +
        'chosen. Consider a darker or lighter brand colour.'
      )
    }
  }

  const secondary = parseHex(branding.secondary_color)
  if (secondary) {
    target.style.setProperty('--accent', toHsl(secondary).css)
    target.style.setProperty('--accent-ink', readableInk(secondary).ink)
  }

  // Font is a preference, not arbitrary CSS: it is interpolated into a font
  // stack, so it is quoted and stripped of anything that could close the
  // declaration and inject a rule.
  if (typeof branding.font_preference === 'string' && branding.font_preference.trim()) {
    const safe = branding.font_preference.replace(/["'\\;{}<>]/g, '').trim().slice(0, 64)
    if (safe) {
      target.style.setProperty(
        '--font-sans',
        `"${safe}", ui-sans-serif, system-ui, -apple-system, sans-serif`
      )
    }
  }

  if (typeof branding.favicon_url === 'string' && branding.favicon_url.trim()) {
    setFavicon(branding.favicon_url.trim())
  }

  return result
}

function setFavicon (href) {
  let link = document.querySelector('link[rel="icon"]')
  if (!link) {
    link = document.createElement('link')
    link.rel = 'icon'
    document.head.appendChild(link)
  }
  link.href = href
}

/**
 * The name to show for a workspace.
 *
 * Order matters and is the §3.4 fallback chain: branding first because a
 * tenant may trade under a different name than the one on their account, then
 * the tenant record, then a neutral last resort that is never blank. A blank
 * header reads as a broken page.
 *
 * @param {{name?: string, branding?: Branding}|null} tenant
 * @returns {string}
 */
export function workspaceName (tenant) {
  return (
    tenant?.branding?.business_name?.trim() ||
    tenant?.name?.trim() ||
    'Workspace'
  )
}

/**
 * Reset to MBOS defaults. Called on sign-out so the next person to use a
 * shared work laptop does not see the previous tenant's colours on the login
 * screen — a small thing that looks exactly like a data leak.
 */
export function clearBranding (target = document.documentElement) {
  for (const prop of [
    '--brand', '--brand-hover', '--brand-subtle', '--brand-ink',
    '--accent', '--accent-ink', '--font-sans'
  ]) {
    target.style.removeProperty(prop)
  }
}
